import { CheckCircle, Eye, HourglassLow, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { useCallback } from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import RegistrationStatusMessage from '@/components/RegistrationStatus/RegistrationStatusMessage';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
    getCatalogCurrency,
    getCatalogIsCatalogOnly,
    getCatalogIsTimedPlus,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogSellerId,
    getCatalogSellerName,
    getCatalogTitle,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    catalogId: number;
    className?: string;
};

export const CatalogRegistrationMessages = ({ catalogId, className }: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const title = useAppSelector((state) => getCatalogTitle(state, catalogId));
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const sellerName = useAppSelector((state) => getCatalogSellerName(state, catalogId));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const limit = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const catalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const isTimedPlusAuction = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const isTimedPlusAuctionOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));

    const showSendSellerMessageModalDeclined = useCallback(() => {
        const messageDetails = {
            catalogId,
            message: String(formatMessage(['message_registration_declined'])),
            title,
        };

        const subject = `${String(formatMessage(['registration_declined']))}: ${title}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, sellerName, sellerId, title]);

    return catalogOnly ? (
        <RegistrationStatusMessage
            alertType="pending"
            className={className}
            header={formatMessage(['registration', 'preview_only'])}
            icon={Eye}
            message={formatMessage(['registration', 'preview_only_description'])}
        />
    ) : declined ? (
        <RegistrationStatusMessage
            alertType="declined"
            className={className}
            header={formatMessage(['registration_declined'])}
            icon={Warning}
            message={
                <>
                    {formatMessage(['auctioneer_has_declined_registration'])}
                    <SubtleLink
                        className="ml-2xs"
                        onClick={showSendSellerMessageModalDeclined}
                    >
                        {formatMessage(['contact_auctioneer'])}
                    </SubtleLink>
                </>
            }
        />
    ) : limit ? (
        <RegistrationStatusMessage
            alertType="approved"
            className={className}
            header={formatMessage(['bid_limit_approval'], {
                values: {
                    limit: (
                        <FormattedCurrency
                            key="catalog-registration-bid-limit"
                            className="justify-center"
                            convert
                            noFraction
                            sourceCurrency={currency}
                            value={bidLimit}
                        />
                    ),
                },
            })}
            icon={CheckCircle}
            justify
            message={formatMessage([
                isTimedPlusAuction && !isTimedPlusAuctionOpen
                    ? 'timedPlusAuctionPendingBidOnAnyItems'
                    : 'you_can_bid_on_any_of_the_items',
            ])}
        />
    ) : approved ? (
        <RegistrationStatusMessage
            alertType="approved"
            className={className}
            header={formatMessage(['approved_to_bid'])}
            icon={CheckCircle}
            message={formatMessage([
                isTimedPlusAuction && !isTimedPlusAuctionOpen
                    ? 'timedPlusAuctionPendingBidOnAnyItems'
                    : 'you_can_bid_on_any_of_the_items',
            ])}
        />
    ) : pending ? (
        <RegistrationStatusMessage
            alertType="pending"
            className={className}
            header={formatMessage(['registration_pending'])}
            icon={HourglassLow}
            message={formatMessage(['well_send_you_an_email'])}
        />
    ) : null;
};
